import { Fragment } from 'react';
import {
	PagedDialog,
	H3,
	H4,
	Spacer,
	Text,
	Label,
	TempChip,
	InfoChip
} from '@components';
import { TShipmentOrder, CHIP_SIZE, CARGO_TYPE_ID } from '@types';
import { EditShipment } from '@state';
import { useRecoilValue } from 'recoil';
import { withPlural, formatDate, getFreightModeLabel, groupCargosByStop, totalCargos } from '@utils';
import ArrowUpIcon from '@mui/icons-material/NorthEast';
import ArrowDownIcon from '@mui/icons-material/SouthEast';
import styles from './EditShipment.module.scss';

export const Preview = () => {
	const shipment = useRecoilValue<TShipmentOrder | null>(EditShipment);
	const getRefs = () => {
		const orderNumbers: string[] = [];
		if (shipment) {
			if (shipment.shipper_ref1 && shipment.shipper_ref1 !== '') orderNumbers.push(shipment.shipper_ref1);
			if (shipment.shipper_ref2 && shipment.shipper_ref2 !== '') orderNumbers.push(shipment.shipper_ref2);
			if (shipment.shipper_ref3 && shipment.shipper_ref3 !== '') orderNumbers.push(shipment.shipper_ref3);
		}
		return orderNumbers.join(', ');
	}
	const getCargoDetails = () => {
		const members: string[] = [];
		if (shipment) {
			const quantity = totalCargos(shipment?.cargos ?? [], 'qty');
			const palletQuantity = totalCargos(shipment?.cargos ?? [], 'pallet_qty');
			switch (shipment.cargo_type_id) {
			case CARGO_TYPE_ID.Loose:
				break;
			case CARGO_TYPE_ID.Other:
				if (quantity) members.push(`${quantity} ${withPlural('item', quantity)}`);
				break;
			case CARGO_TYPE_ID.CustomItem:
				if (palletQuantity !== null) members.push(`${palletQuantity} ${withPlural('item', palletQuantity)}`);
				// An item booking always carries exactly one cargo, so its volume is the shipment's.
				if (shipment.cargos[0]?.total_cubic_volume_mts) members.push(shipment.cargos[0].total_cubic_volume_mts);
				break;
			default:
				if (palletQuantity !== null) members.push(`${palletQuantity} ${withPlural('pallet', palletQuantity)}`);
				if (quantity !== null) members.push(`${quantity} ${withPlural('space', quantity)}`);
				break;
			}
			const totalWeight = totalCargos(shipment.cargos, 'total_weight') ?? 0;
			if (totalWeight) members.push(`${totalWeight}kg`);
		}
		return <Text>{members.join(' | ')}</Text>;
	}
	const getLoadTruckDetails = () => {
		const members: string[] = [];
		if (shipment) {
			if (shipment.cargo_type && shipment.cargo_type !== '') members.push(shipment.cargo_type);
			if (shipment.freight_mode) members.push(getFreightModeLabel(shipment.freight_mode));
			if (shipment.req_truck_size && shipment.req_truck_size.name) members.push(shipment.req_truck_size.name);
			if (shipment.req_truck_type && shipment.req_truck_type.name) members.push(shipment.req_truck_type.name);
		}
		return members.join(' | ');
	}
	const getDateTimes = (dest: 'pickup' | 'dropoff', index: number) => {
		const theDate = formatDate(new Date(shipment?.cargos[index][dest === 'pickup' ? 'pickup_at' : 'drop_at'] ?? new Date()), 'DATE_WITH_SHORT_MONTH_NAME');
		const timeFrom = dest === 'pickup' ? shipment?.cargos[index].pick_from : shipment?.cargos[0].drop_from;
		const timeTo = dest === 'pickup' ? shipment?.cargos[index].pick_to : shipment?.cargos[0].drop_to;
		return `${theDate} | ${timeFrom} - ${timeTo}`;
	}
	const getDestDetails = (dest: 'pickup' | 'dropoff', index: number) => {
		return (
			<>
				<div className={`${styles.row} ${styles.spaceBetween}`}>
					<Label>{shipment?.cargos[index][dest].business_name}</Label>
					<InfoChip text={dest === 'pickup' ? 'Pick-up' : 'Drop-off'} size={CHIP_SIZE.SMALL} CustomIcon={dest === 'pickup'? ArrowUpIcon : ArrowDownIcon} />
				</div>
				<Text>{shipment?.cargos[index][dest].address}</Text>
				<Text>{getDateTimes(dest, index)}</Text>
			</>
		)
	}
	return (
		<PagedDialog.Preview>
			<H3>Booking Details</H3>
			<Spacer size={20} divider />
			<H4>{shipment?.reference}</H4>
			<Spacer size={5} />
			<Text>{getRefs()}</Text>
			<Spacer size={5} />
			<div className={styles.row}>
				<TempChip name={shipment?.temperature_id ?? 1} size={CHIP_SIZE.SMALL} />{getCargoDetails()}
			</div>
			<Spacer size={5} />
			<Text>{getLoadTruckDetails()}</Text>
			<Spacer size={20} divider />
			{/* Cargos collected from the same place are one stop, and there is a single drop-off
				however many cargos the shipment carries. */}
			{groupCargosByStop(shipment?.cargos ?? []).pickups.map((group, i) => {
				return (
					<Fragment key={i}>
						{getDestDetails('pickup', group[0])}
						<Spacer size={20} />
					</Fragment>
				)
			})}
			{!!shipment?.cargos.length && getDestDetails('dropoff', shipment.cargos.length - 1)}
		</PagedDialog.Preview>
	);
}
Preview.displayName = 'Preview';
